import { Button, Input } from "@strivelabs/ui-core"
import { Eye, EyeOff } from "lucide-react"
import { forwardRef, useState, type InputHTMLAttributes } from "react"

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type">

/**
 * Single-line password field with a built-in show/hide toggle. Forwards refs
 * so it slots cleanly into react-hook-form's `register(...)`. Wherever you'd
 * write a `<Input type="password" />` plus an `Eye/EyeOff` button, use this
 * instead.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
    function PasswordInput ({ className, ...props }, ref) {
        const [ show, setShow ] = useState(false)
        const inputClassName = [ "pr-10", className ].filter(Boolean).join(" ")

        return (
            <div className="relative">
                <Input
                    ref={ref}
                    type={show ? "text" : "password"}
                    className={inputClassName}
                    {...props}
                />
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    tabIndex={-1}
                    aria-label={show ? "Hide password" : "Show password"}
                    onClick={() => setShow((v) => { return !v })}
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
            </div>
        )
    }
)
